import React from "react";
import { Link } from "react-router-dom";
import PageHero from "@/components/PageHero";
import Section from "@/components/Section";
import {
  Bed, Stethoscope, Video, Phone, FileText, ShieldCheck,
  Activity, UserPlus, HeartPulse, Pill, ClipboardList, Star, ArrowRight
} from "lucide-react";

const categories = [
  { icon: Bed, title: "Hospital Bed Booking", lines: ["SOS / emergency bookings", "Scheduled bed bookings", "Multiple bed categories", "Refund if not admitted"] },
  { icon: Stethoscope, title: "Home Healthcare Visits", lines: ["Doctors, Nurses, Physios", "Dieticians & Medical Technologists", "Trained Ayahs / Attendants", "Pay online through the app"] },
  { icon: Video, title: "Video Consultations", lines: ["Hospital doctors, physios, dieticians", "HomeCure doctors and dieticians anytime", "Nurses & Physios after 1st visit", "Telemedicine Guidelines 2020 compliant"] },
  { icon: Phone, title: "Masked Audio Calls", lines: ["Phone numbers never disclosed", "Virtual proxy number routing", "90-day call metadata retention", "Audio content never recorded"] },
  { icon: FileText, title: "Health Records Vault", lines: ["Prescriptions, reports & scans", "AES-256 encryption at rest", "Delete anytime from app", "Share only with explicit consent"] },
  { icon: ShieldCheck, title: "Health Profile & Insurance", lines: ["Store comorbidities, allergies", "List health insurance providers", "Emergency contact details", "Used only to improve your care"] },
  { icon: Star, title: "Reviews & Ratings", lines: ["Rate personnel after appointments", "Rate hospital doctors & departments", "Rate the hospital overall", "Moderated for fairness"] },
  { icon: HeartPulse, title: "SOS Emergency", lines: ["GPS-based hospital matching", "Auto-notify emergency contact", "Share health profile with consent", "Records kept 3 years (medicolegal)"] },
];

export default function Services() {
  return (
    <>
      <PageHero
        title="Services"
        subtitle="From SOS hospital bed bookings to home healthcare visits, secure telemedicine and a personal health records vault — everything you need from one trusted platform."
      />

      <Section eyebrow="Everything we do" title="Healthcare services across three tiers" center>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {categories.map((s, i) => (
            <div key={i} className="hc-card p-6" data-testid={`service-detail-${i}`}>
              <div className="w-11 h-11 rounded-lg bg-[#E8F0FB] flex items-center justify-center text-[#3780E0]">
                <s.icon className="w-5 h-5" />
              </div>
              <div className="mt-3 font-bold text-lg text-[#0F2A4A]">{s.title}</div>
              <ul className="mt-2 text-sm text-slate-600 space-y-1.5">
                {s.lines.map((l, idx) => <li key={idx} className="flex gap-2"><span className="text-[#3780E0]">•</span> {l}</li>)}
              </ul>
            </div>
          ))}
        </div>
      </Section>

      <Section bg="light" eyebrow="Categories of personnel" title="Verified healthcare professionals" center>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          {[
            { icon: Stethoscope, label: "Doctors" },
            { icon: HeartPulse, label: "Nurses" },
            { icon: Activity, label: "Physiotherapists" },
            { icon: Pill, label: "Dieticians" },
            { icon: ClipboardList, label: "Medical Technologists" },
            { icon: UserPlus, label: "Ayahs / Attendants" },
          ].map((p, i) => (
            <div key={i} className="bg-white rounded-xl border border-[#DDE7F7] p-5 text-center hover:border-[#3780E0] transition">
              <p.icon className="w-7 h-7 mx-auto text-[#3780E0]" />
              <div className="mt-2 font-semibold text-[#0F2A4A] text-sm">{p.label}</div>
            </div>
          ))}
        </div>
      </Section>

      <Section bg="primary" center>
        <h2 className="text-white text-3xl md:text-4xl font-extrabold">Want to use HomeCure when we launch?</h2>
        <p className="text-white/90 mt-3">Reach out to us — our team will keep you updated.</p>
        <div className="mt-6 flex justify-center gap-3 flex-wrap">
          <Link to="/contact" className="bg-white text-[#2563B3] hover:bg-slate-100 font-bold px-6 py-3 rounded-lg inline-flex items-center gap-2">Contact us <ArrowRight className="w-4 h-4" /></Link>
          <Link to="/partner" className="bg-[#0F2A4A] hover:bg-[#0B2240] text-white font-bold px-6 py-3 rounded-lg">Partner with us</Link>
        </div>
      </Section>
    </>
  );
}
